import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * variant별 기본 태그
 * - 키는 _renewal_component.scss의 Txt 부분의 $re_typo 키, propTypes의 variant 값과 반드시 같아야 함
 * - as prop으로 바꿀 수 있음
 * - 헤딩 단계는 페이지 구조(CustomSubHeader 등)에 맞춰 조정할 것
 */
const DEFAULT_TAG = {
	h_page: 'h2',
	h_section: 'h3',
	h_card: 'h4',
	num_lg: 'span',
	num_md: 'span',
	body: 'p',
	body_lg: 'p',
	caption: 'span',
	tag: 'span',
};

const Txt = forwardRef(
	(
		{
			as = null,
			variant = 'body',
			weight = null,
			moWeight = null,
			color = 'black',
			className = null,
			children,
			...props
		},
		ref,
	) => {
		// 우선순위: as → variant 기본 태그 → p
		const Tag = as || DEFAULT_TAG[variant] || 'p';

		return (
			<Tag
				ref={ref}
				className={classNames(
					're_txt',
					{
						[`txt_${variant}`]: variant,
						[`fw_${weight}`]: weight,
						[`fw_mo_${moWeight}`]: moWeight,
						[`txt_c_${color}`]: color,
					},
					className,
				)}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}>
				{children}
			</Tag>
		);
	},
);

Txt.propTypes = {
	children: PropTypes.node,
	/**
	 * 실제 HTML 태그 (의미 기준). 없으면 variant별 기본 태그 사용
	 */
	as: PropTypes.oneOf([
		null,
		'h1',
		'h2',
		'h3',
		'h4',
		'h5',
		'h6',
		'p',
		'span',
		'strong',
		'div',
	]),
	/**
	 * 글자 크기 그룹 (시안 텍스트 스타일 기준, PC/모바일 자동 전환)
	 */
	variant: PropTypes.oneOf([
		'h_page', // h: heading
		'h_section',
		'h_card',
		'num_lg', // num: number
		'num_md',
		'body',
		'body_lg',
		'caption',
		'tag',
	]),
	/**
	 * 굵기 (없으면 variant 기본 굵기)
	 * body / body_lg의 medium은 모바일에서 400으로 자동 변환됨
	 */
	weight: PropTypes.oneOf([null, 'semibold', 'medium', 'regular']),
	/**
	 * 모바일 전용 굵기 (자동 변환 대신 직접 지정할 때만 사용)
	 */
	moWeight: PropTypes.oneOf([null, 'semibold', 'medium', 'regular']),
	/**
	 * 리뉴얼 컬러 (_renewal_component의 Txt 부분의 $re_txt_colors와 같아야 함)
	 */
	color: PropTypes.oneOf([
		null,
		'blue',
		'green',
		'red',
		'gray',
		'gray2',
		'black',
		'black2',
		'black3',
		'white',
		'purple',
		'lavender'
	]),
	className: PropTypes.string,
};

Txt.displayName = 'Txt';

export default Txt;